import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Button} from '../../atoms/Buttons/Button';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {GenericIconName, IconName} from '../../atoms/Icon/IconName';
import {SliderControlProps} from '../../molecules/HorizontalSlider/types';

export const ProductTileImageSliderSideButtons: FC<SliderControlProps> = ({
  prev,
  next,
  nextIsActive,
  prevIsActive,
  navButtonsZIndex = 1,
  maxPages,
  loop = false,
}) => {
  const {t} = useTranslation();
  if (maxPages === 1) {
    return null;
  }
  return (
    <>
      <NavButton
        onClick={prev}
        isActive={prevIsActive}
        iconName={GenericIconName.ArrowLeft}
        navButtonsZIndex={navButtonsZIndex}
        data-testid="arrowLeft"
        aria-label={t('Go to the next slide')}
      />
      <NavButton
        onClick={next}
        isActive={nextIsActive || loop}
        iconName={GenericIconName.ArrowRight}
        navButtonsZIndex={navButtonsZIndex}
        isNextButton
        data-testid="arrowRight"
        aria-label={t('Go to the next slide')}
      />
    </>
  );
};

export const NavButton: FC<{
  onClick: VoidFunction;
  isActive: boolean;
  iconName: IconName;
  iconSize?: keyof typeof IconSize;
  isNextButton?: boolean;
  className?: string;
  'data-testid'?: string;
  navButtonsZIndex?: number;
  'aria-label': string;
}> = ({
  onClick,
  isActive,
  iconName,
  iconSize = 'SS',
  isNextButton,
  className,
  'data-testid': dataTestId,
  navButtonsZIndex,
  'aria-label': ariaLabel,
}) => {
  return (
    <Fade mounted={isActive} duration={300}>
      <FloatingWrapper
        onClick={onClick}
        data-next-button={isNextButton}
        className={className}
        data-testid={dataTestId}
        $navButtonsZIndex={navButtonsZIndex}
      >
        <StyledCircleButton
          aria-label={ariaLabel}
          size="sm"
          variant="floating"
          round={true}
        >
          <Icon size={iconSize} asset={iconName} />
        </StyledCircleButton>
      </FloatingWrapper>
    </Fade>
  );
};

export const StyledCircleButton = styled(Button)`
  &&& {
    min-height: 32px;
    width: 32px;
    padding: 5px;
  }
`;

const FloatingWrapper = linariaStyled.span<{
  $navButtonsZIndex?: number;
}>`
  --distance-from-border: 8px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: var(--distance-from-border);
  background: ${COLORS.White};
  backdrop-filter: blur(6px);
  border-radius: 50%;
  box-shadow: 0px 3px 12px 2px rgba(0, 0, 0, 0.07);
  z-index: ${({$navButtonsZIndex}) => $navButtonsZIndex ?? ''};

  &[data-next-button='true'] {
    left: initial;
    right: var(--distance-from-border);
  }
`;
